import { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import { FaExclamationCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axiosInstance from '../../axiosInstance';
import { API_URL_All } from '../../api';
import * as general from '../../libs/general';

const TTH_STATUS_OPTIONS = ["Đơn mới", "Khách CK", "Thành công", "Đơn hủy"];

const emptyModel = {
  id: null,
  customerCode: '',
  partnerCode: '',
  currency: 'CNY',
  quantity: '',
  customerRate: '',
  partnerRate: '',
  statusName: '',
  note: '',
};

/**
 * Modal tạo/sửa Đơn TTH — tỷ giá (KH + ĐT) nhập tay trực tiếp trên form (không có màn quản lý tỷ
 * giá riêng ở MVP này). Server luôn tính lại amountCustomer/amountPartner/amountDiff khi lưu; số
 * hiển thị bên dưới chỉ là preview client-side.
 */
const TthOrderEditor = ({ show, onHide, orderId = null, onSaved }) => {
  const [model, setModel] = useState(emptyModel);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    if (!show) return;
    if (orderId) {
      setLoading(true);
      axiosInstance.get('/api/tth-orders/getTthOrder', { params: { id: orderId } })
        .then((res) => {
          if (!res.data?.success) {
            toast.error(res.data?.message || 'Không tải được đơn TTH');
            return;
          }
          const d = res.data.data;
          setModel({
            id: d.id,
            customerCode: d.customerCode || '',
            partnerCode: d.partnerCode || '',
            currency: d.currency || 'CNY',
            quantity: d.quantity ?? '',
            customerRate: d.customerRate ?? '',
            partnerRate: d.partnerRate ?? '',
            statusName: d.statusName || '',
            note: d.note || '',
          });
          setLocked(Boolean(d.locked));
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    } else {
      setModel(emptyModel);
      setLocked(false);
    }
  }, [show, orderId]);

  const preview = useMemo(() => {
    const qty = Number(model.quantity) || 0;
    const cRate = Number(model.customerRate) || 0;
    const pRate = Number(model.partnerRate) || 0;
    const amountCustomer = qty * cRate;
    const amountPartner = qty * pRate;
    return {
      amountCustomer,
      amountPartner,
      amountDiff: amountCustomer - amountPartner,
    };
  }, [model.quantity, model.customerRate, model.partnerRate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!model.customerCode?.trim()) { toast.warning('Vui lòng nhập Mã KH'); return; }
    if (!model.partnerCode?.trim()) { toast.warning('Vui lòng nhập Mã Đối tác'); return; }
    if (!(Number(model.quantity) > 0)) { toast.warning('Số lượng Tệ phải lớn hơn 0'); return; }
    if (!(Number(model.customerRate) > 0)) { toast.warning('Tỷ giá khách hàng phải lớn hơn 0'); return; }
    if (!(Number(model.partnerRate) > 0)) { toast.warning('Tỷ giá đối tác phải lớn hơn 0'); return; }

    setSubmitting(true);
    try {
      const body = {
        id: model.id,
        customerCode: model.customerCode.trim(),
        partnerCode: model.partnerCode.trim(),
        currency: model.currency || 'CNY',
        quantity: Number(model.quantity),
        customerRate: Number(model.customerRate),
        partnerRate: Number(model.partnerRate),
        statusName: model.statusName || undefined,
        note: model.note,
      };
      const res = await axiosInstance.put('/api/tth-orders/saveTthOrder', body);
      if (!res.data?.success) {
        toast.error(res.data?.message || 'Lưu đơn TTH thất bại');
        return;
      }
      toast.success(res.data.message || 'Lưu đơn TTH thành công');
      onSaved?.();
      onHide();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{model.id ? `Sửa đơn TTH #${model.id}` : 'Tạo đơn TTH'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : (
            <>
              {locked && (
                <div className="alert alert-warning py-2">
                  Đơn đã khóa (đã đối soát) — chỉ có thể xem, không sửa được.
                </div>
              )}
              <Row className="g-3">
                <Col md={6}>
                  <Form.Label>Mã KH <span className="text-danger">*</span></Form.Label>
                  <div className="th filter-editor">
                    <span className="filter-ib" data-source-url={API_URL_All + '/api/users/searchUserCodes'}>
                      <input
                        type="text"
                        className="form-control"
                        name="customerCode"
                        placeholder="Nhập mã khách hàng..."
                        disabled={locked}
                        value={model.customerCode ?? ''}
                        onChange={(e) => setModel({ ...model, customerCode: e.target.value })}
                      />
                    </span>
                  </div>
                </Col>
                <Col md={6}>
                  <Form.Label>Mã Đối tác <span className="text-danger">*</span></Form.Label>
                  <div className="th filter-editor">
                    {/* Chỉ gợi ý đối tác đã được gán Line "Line TTH" (xem Contact.jsx —
                        gán Line cho đối tác), dùng đúng cơ chế lọc theo Line có sẵn
                        (searchContactNames + data-refer) mà OrderEditor.jsx đang dùng cho Line
                        thường/VAT, thay vì searchContactCodes vốn trả về TẤT CẢ đối tác. */}
                    <span className="filter-ib" data-source-key="code" data-source-url={API_URL_All + '/api/contacts/searchContactNames'}>
                      <input
                        type="text"
                        className="form-control"
                        name="partnerCode"
                        placeholder="Nhập mã đối tác..."
                        disabled={locked}
                        data-refer="Line TTH"
                        value={model.partnerCode ?? ''}
                        onChange={(e) => setModel({ ...model, partnerCode: e.target.value })}
                      />
                    </span>
                  </div>
                </Col>

                <Col md={4}>
                  <Form.Label>Loại tiền</Form.Label>
                  <Form.Control
                    type="text"
                    value={model.currency}
                    disabled={locked}
                    onChange={(e) => setModel({ ...model, currency: e.target.value })}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Số lượng Tệ <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="number" min="0" step="0.01"
                    value={model.quantity}
                    disabled={locked}
                    onChange={(e) => setModel({ ...model, quantity: e.target.value })}
                  />
                </Col>
                {model.id && (
                  <Col md={4}>
                    <Form.Label>Trạng thái đơn</Form.Label>
                    <Form.Select
                      value={model.statusName}
                      disabled={locked}
                      onChange={(e) => setModel({ ...model, statusName: e.target.value })}
                    >
                      <option value="">-- Giữ nguyên --</option>
                      {TTH_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </Form.Select>
                  </Col>
                )}

                <Col md={6}>
                  <Form.Label>
                    Tỷ giá ĐT (VND/đơn vị) <span className="text-danger">*</span>{' '}
                    <FaExclamationCircle
                      className="text-primary"
                      style={{ cursor: 'help' }}
                      title={`Nhập tỷ giá cho 1 ${model.currency || 'CNY'}`}
                    />
                  </Form.Label>
                  <Form.Control
                    type="number" min="0" step="0.0001"
                    value={model.partnerRate}
                    disabled={locked}
                    onChange={(e) => setModel({ ...model, partnerRate: e.target.value })}
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>
                    Tỷ giá KH (VND/đơn vị) <span className="text-danger">*</span>{' '}
                    <FaExclamationCircle
                      className="text-primary"
                      style={{ cursor: 'help' }}
                      title={`Nhập tỷ giá cho 1 ${model.currency || 'CNY'}`}
                    />
                  </Form.Label>
                  <Form.Control
                    type="number" min="0" step="0.0001"
                    value={model.customerRate}
                    disabled={locked}
                    onChange={(e) => setModel({ ...model, customerRate: e.target.value })}
                  />
                </Col>

                <Col xs={12}>
                  <div className="border rounded p-2 bg-light small">
                    <Row>
                      <Col md={4}>VNĐ KH: <b>{general.formatNumber(preview.amountCustomer)}</b></Col>
                      <Col md={4}>VNĐ ĐT: <b>{general.formatNumber(preview.amountPartner)}</b></Col>
                      <Col md={4}>Chênh lệch dự kiến: <b>{general.formatNumber(preview.amountDiff)}</b></Col>
                    </Row>
                  </div>
                </Col>

                <Col xs={12}>
                  <Form.Label>Ghi chú</Form.Label>
                  <Form.Control
                    as="textarea" rows={2}
                    value={model.note}
                    disabled={locked}
                    onChange={(e) => setModel({ ...model, note: e.target.value })}
                  />
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={submitting}>Đóng</Button>
          {!locked && (
            <Button type="submit" variant="primary" disabled={submitting || loading}>
              {submitting ? <Spinner size="sm" animation="border" /> : 'Lưu'}
            </Button>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default TthOrderEditor;
